import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  getGoogleAdsAccountsApiV1IntegrationsGoogleAdsAccountsGet,
  getGoogleAdsCampaignsApiV1IntegrationsGoogleAdsCampaignsGet,
  getGoogleAdsKeywordsApiV1IntegrationsGoogleAdsKeywordsGet,
  getGoogleAdsStatusApiV1IntegrationsGoogleAdsStatusGet,
} from "@/backend-sdk/client/sdk.gen";
import { isHeyApiError } from "@/lib/hey-api-client";
import authService from "@/services/auth.service";

const getDebugErrorStatus = (error: unknown): string | number => {
  if (isHeyApiError(error)) return error.status;
  return "desconhecido";
};

const getDebugErrorDetails = (error: unknown): string => {
  if (isHeyApiError(error)) {
    return JSON.stringify(error.data ?? error.detail ?? error.message);
  }
  if (error instanceof Error) {
    return error.message;
  }
  return JSON.stringify(error);
};

const DebugPage = () => {
  const [results, setResults] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    email: "joao.silva@exemplo.com",
    password: "123456",
  });

  const addResult = (message: string) => {
    setResults((prev) => [
      ...prev,
      `[${new Date().toLocaleTimeString()}] ${message}`,
    ]);
  };

  const testFullFlow = async () => {
    setLoading(true);
    setResults([]);

    try {
      addResult("🔍 Iniciando teste completo...");

      // 0. Primeiro fazer login
      addResult("0. Fazendo login...");
      const tokenData = await authService.login(credentials);
      addResult(
        `✅ Login bem-sucedido! Token: ${tokenData.access_token.substring(0, 20)}...`,
      );

      // 1. Testar Status
      addResult("1. Testando getStatus...");
      const { data: status } =
        await getGoogleAdsStatusApiV1IntegrationsGoogleAdsStatusGet({
          throwOnError: true,
        });
      addResult(`✅ Status: ${JSON.stringify(status)}`);

      if (status.is_connected) {
        // 2. Testar Accounts
        addResult("2. Testando getAccounts...");
        const { data: accounts } =
          await getGoogleAdsAccountsApiV1IntegrationsGoogleAdsAccountsGet({
            throwOnError: true,
          });
        addResult(`✅ Contas: ${accounts.length} encontradas`);

        accounts.forEach((acc, i) => {
          addResult(`   ${i + 1}. ${acc.account_name} (${acc.account_id})`);
        });

        if (accounts.length > 0) {
          const accountId = accounts[0].account_id;

          // 3. Testar Campaigns
          addResult("3. Testando getCampaigns...");
          const { data: campaigns } =
            await getGoogleAdsCampaignsApiV1IntegrationsGoogleAdsCampaignsGet({
              query: { account_id: accountId },
              throwOnError: true,
            });
          addResult(`✅ Campanhas: ${campaigns.length} encontradas`);

          campaigns.forEach((camp, i) => {
            addResult(
              `   ${i + 1}. ${camp.campaign_name}: ${camp.impressions} impressões`,
            );
          });

          // 4. Testar Keywords
          addResult("4. Testando getKeywords...");
          const { data: keywords } =
            await getGoogleAdsKeywordsApiV1IntegrationsGoogleAdsKeywordsGet({
              query: { account_id: accountId },
              throwOnError: true,
            });
          addResult(`✅ Keywords: ${keywords.length} encontradas`);

          keywords.forEach((kw, i) => {
            addResult(
              `   ${i + 1}. ${kw.keyword_text}: ${kw.impressions} impressões`,
            );
          });

          addResult("🎉 TODOS OS TESTES PASSARAM!");
          addResult("✅ O frontend está funcionando perfeitamente!");
          addResult("✅ Agora vá para /dashboard e teste Google Ads no menu");
        } else {
          addResult("❌ Nenhuma conta encontrada (mas status está conectado)");
        }
      } else {
        addResult("❌ Google Ads não conectado");
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Erro desconhecido";
      addResult(`❌ Erro: ${message}`);
      addResult(`   Status: ${getDebugErrorStatus(error)}`);
      addResult(`   Detalhes: ${getDebugErrorDetails(error)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Debug - Teste Completo (Login + APIs)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={credentials.email}
                onChange={(e) =>
                  setCredentials((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) =>
                  setCredentials((prev) => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={testFullFlow} disabled={loading}>
              {loading ? "Testando..." : "Testar Login + APIs"}
            </Button>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
            <pre className="text-sm">
              {results.length === 0
                ? "Clique em um dos botões para começar..."
                : results.join("\n")}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DebugPage;
